import { ColumnDef, ColumnPullStatus, PullStatusResponse } from 'src/models/segment-cdt/SegmentCdtTypes';

/** The Cloudscape `StatusIndicator` types this feature uses. */
export type PullIndicatorType = 'success' | 'error' | 'warning' | 'info' | 'in-progress' | 'pending';

export interface PullPresentation {
    type: PullIndicatorType;
    /** What a person reads. Says which stage, or which of the four outcomes — never a category. */
    label: string;
}

/**
 * Each lifecycle value's indicator and wording. One table, because the API reports one status: the
 * execution's own value, which already says both where a running pull has reached and how a finished one
 * ended.
 *
 * Three choices worth stating, because each is the difference between a true label and a plausible one:
 *
 * - `PARTIAL_FAILURE` is a **warning**, not an error and not a success. Some columns pulled and some did
 *   not, so a red cross overstates it and a green tick would let a half-populated period be signed off.
 * - `NO_CONFIG` is **info**: the table has no GL columns, so nothing was pulled and nothing went wrong.
 *   Rendering it as "Completed" would claim a pull happened.
 * - `SKIPPING_GL` reads as a stage in progress, because it is one — the execution has no GL columns to
 *   fetch but still runs its derive stage, so a poller must keep polling.
 */
export const STAGE_PRESENTATION: Record<ColumnPullStatus, PullPresentation> = {
    [ColumnPullStatus.PENDING]: { type: 'pending', label: 'Queued' },
    [ColumnPullStatus.READING_CONFIG]: { type: 'in-progress', label: 'Reading column configuration' },
    [ColumnPullStatus.PULLING_GL]: { type: 'in-progress', label: 'Pulling GL balances' },
    [ColumnPullStatus.SKIPPING_GL]: { type: 'in-progress', label: 'Skipping GL pull' },
    [ColumnPullStatus.DERIVING]: { type: 'in-progress', label: 'Deriving values' },
    [ColumnPullStatus.SUCCESS]: { type: 'success', label: 'Completed' },
    [ColumnPullStatus.PARTIAL_FAILURE]: { type: 'warning', label: 'Partially failed' },
    [ColumnPullStatus.FAILED]: { type: 'error', label: 'Failed' },
    [ColumnPullStatus.NO_CONFIG]: { type: 'info', label: 'Nothing to pull' },
};

/**
 * How to show one status.
 *
 * A value not in the table is reported **verbatim under `info`** rather than being recategorised. A
 * contract addition then appears as itself — a status nobody has taught this screen about yet — instead of
 * being silently absorbed into whichever category happened to be the fallback.
 *
 * Returns undefined only when no status is given, which is "nothing to state".
 */
export function pullPresentation(status?: ColumnPullStatus): PullPresentation | undefined {
    if (!status) {
        return undefined;
    }
    return STAGE_PRESENTATION[status] ?? { type: 'info', label: String(status) };
}

/**
 * Whether a pull has stopped, from the server's own `terminal`.
 *
 * **Deliberately not derived from `overallStatus`.** The stored lifecycle knows which of its members are
 * endings and the API reports that answer directly, so this reads it rather than reimplementing it. A local
 * list would have to be kept correct forever by whoever adds a lifecycle member, with nothing to tell them —
 * and it would have to encode the one genuinely counter-intuitive case, `SKIPPING_GL`, which reads like an
 * ending and is not one because the derive stage still runs.
 *
 * An absent value is **not** terminal: it means the record stated no status at all, and stopping a poll on a
 * pull that may still be running is the worse failure. Polling one stage too long costs a request; stopping
 * early leaves a half-populated period behind a screen claiming it finished.
 */
export function isTerminal(pull?: Pick<PullStatusResponse, 'terminal'>): boolean {
    return pull?.terminal === true;
}

/** The outcomes that mean something did not pull. `NO_CONFIG` is not one: nothing was asked of it. */
const FAILURE_STAGES: ColumnPullStatus[] = [
    ColumnPullStatus.PARTIAL_FAILURE,
    ColumnPullStatus.FAILED,
];

/**
 * Whether a status says something failed to pull.
 *
 * Still a local set, because this is not terminality: it is the narrower "did work go unwritten", which the
 * server does not report and which drives the warning-versus-error distinction rather than any control flow.
 * Getting it wrong mislabels a banner; it cannot wedge a poll. That is why the two are not symmetrical.
 */
export function isFailureStage(status?: ColumnPullStatus): boolean {
    return !!status && FAILURE_STAGES.includes(status);
}

/**
 * The only two per-column statuses that mean a column has finished.
 *
 * There is no per-column `terminal`, and there does not need to be: `configStatuses` carries a documented
 * four-value subset — `PENDING` seeded by `ReadGLConfigComponent`, then `PULLING_GL`, `SUCCESS` or `FAILED`
 * written by the per-column worker — so the closed set here is two members rather than the nine a run can
 * report. Anything else, including a value added later, reads as not finished, which is the safe direction.
 */
const FINISHED_COLUMN_STATUSES: ColumnPullStatus[] = [
    ColumnPullStatus.SUCCESS,
    ColumnPullStatus.FAILED,
];

/**
 * The `errors` keys that name the run rather than a column, so they are not mistaken for column ids.
 * Mirrors the server's own reserved keys; anything else in that map is a column.
 */
export const RESERVED_ERROR_KEYS = ['execution', 'workflow'];

/**
 * Joins reasons for display: deduplicated, because one GL outage fails every column with the same message
 * and repeating it once per column would bury the fact that there is only one thing wrong. Blanks dropped.
 *
 * Returns undefined for nothing to say, so a caller can tell "no reason" from "an empty reason".
 */
export function joinReasons(reasons: (string | undefined)[]): string | undefined {
    const distinct = Array.from(new Set(reasons.filter((reason): reason is string => !!reason)));
    return distinct.length ? distinct.join('; ') : undefined;
}

/**
 * Why the **run** failed, in the server's own words — the failure that belongs to no single column, such as
 * a workflow that timed out or never started.
 *
 * Two sources, and the second one is the point: `failureReason` if the server sends it, otherwise the
 * **reserved keys of `errors`**, which is where that same text lives on a server that does not send the
 * field yet. Read directly so this screen reports a run-level failure against the currently deployed API
 * rather than waiting for the field to ship — and that is precisely the case with no per-column reason to
 * fall back on, so before this it appeared nowhere at all.
 *
 * **Deliberately does not fall back to the per-column reasons.** Those belong to columns, and every surface
 * here already shows them against their column — a row in the breakdown, a banner naming the column. Joining
 * them into a run-level blob as well said the same thing twice, in the less useful form. Nothing is composed:
 * every character comes from the server.
 *
 * Returns undefined when the run recorded no failure of its own, which is every success and every pull that
 * failed only in specific columns.
 */
export function runFailureText(pullStatus?: PullStatusResponse): string | undefined {
    if (pullStatus?.failureReason) {
        return pullStatus.failureReason;
    }
    const errors = pullStatus?.errors ?? {};
    return joinReasons(RESERVED_ERROR_KEYS.map((key) => errors[key]));
}

export interface ColumnPullRow {
    columnId: string;
    /** The column's display name, or its id when the definition has no column by that id. */
    label: string;
    presentation?: PullPresentation;
    /** The status itself, for a caller that needs to decide rather than render. */
    status?: ColumnPullStatus;
    error?: string;
    /** The rate the pull applied, or the literal `MULTIPLE`. */
    fxRate?: string;
}

/**
 * Whether this screen has been taught about a status. An unrecognised value is a contract addition, and the
 * honest reading of it is "unknown", not "no".
 */
export function isKnownStage(status?: ColumnPullStatus): boolean {
    return !!status && !!STAGE_PRESENTATION[status];
}

/**
 * Whether one column's pull has finished.
 *
 * A column with no status has not finished — the absence is "not stated", and announcing a column as done
 * on the strength of a missing field would be a claim the server never made. That case is real: the pull
 * records a column's failure reason before its status, so a column can appear here with an error and no
 * verdict, and it must not then be announced as finished.
 */
export function isColumnFinished(row: ColumnPullRow): boolean {
    return !!row.status && FINISHED_COLUMN_STATUSES.includes(row.status);
}

/**
 * Whether that column failed. A column is called failed only on a status that says so, never on the presence
 * of an error message, because a column can carry a reason recorded before a retry that then succeeded.
 */
export function isColumnFailed(row: ColumnPullRow): boolean {
    return isFailureStage(row.status);
}

/**
 * One row per column the pull has anything to say about, for the per-column breakdown.
 *
 * **Keyed on the union of the three maps, not on `configStatuses` alone.** A column can carry a reason with
 * no status — the pull records the reason the moment it fails, and the status write is a separate
 * conditional update that a terminal record refuses — so keying on statuses alone drops exactly the columns
 * a user is looking for. A row with no status renders its reason and no verdict, which is what is known.
 *
 * The reserved run-level keys are excluded: they are not columns, and they are surfaced by
 * {@link runFailureText} instead.
 *
 * Order follows `configStatuses` first (the pull's own order, stable across polls), then any column known
 * only from an error or a rate, so the list does not reshuffle between ticks.
 */
export function columnPullRows(pullStatus?: PullStatusResponse, columns?: ColumnDef[]): ColumnPullRow[] {
    const statuses = pullStatus?.configStatuses ?? {};
    const errors = pullStatus?.errors ?? {};
    const fxRates = pullStatus?.fxRates ?? {};

    const columnIds: string[] = [];
    for (const key of [...Object.keys(statuses), ...Object.keys(errors), ...Object.keys(fxRates)]) {
        if (!RESERVED_ERROR_KEYS.includes(key) && !columnIds.includes(key)) {
            columnIds.push(key);
        }
    }

    return columnIds.map((columnId) => ({
        columnId,
        label: (columns ?? []).find((c) => c.columnId === columnId)?.displayName ?? columnId,
        presentation: pullPresentation(statuses[columnId]),
        status: statuses[columnId],
        error: errors[columnId],
        fxRate: fxRates[columnId],
    }));
}
